import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { RHFUploadSingleFileFull } from 'src/components/hook-form/rhf-upload';
import RHFRadioGroupProduct from 'src/modules/benefits/coupon-details/components/rhf-radio-group-produc';
import RHFEditor from 'src/components/hook-form/rhf-editor';
import { useMyDataStore } from 'src/store/me/me.store';
import CommentDialog from './commentary-dialog';
import RefuseDialog from './refuse-dialog';
import ApproveDialog from './approve-dialog';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';

type ProductFormProps = {
  hook: any;
};

export default function ProductForm({ hook }: ProductFormProps) {
  const theme = useTheme();

  const user = useMyDataStore((state) => state.me);

  const hasRequiredRole = user?.roles?.some((role: string) =>
    ['lp_shopkeeper', 'lp_exhibitor', 'lp_contractor'].includes(role)
  );

  const disableUpdate =
    hook.isEdit &&
    hook?.productDetails?.lpEventsData?.creatorId !== user?._id &&
    user?.roles.includes('lp_contractor');

  const isCnAdmin = user?.roles?.some(
    (role: string) => role === 'cn_admin' || role === 'lp_promoter'
  );

  const isLpExhibitor = user?.roles?.some((role: string) => role === 'lp_exhibitor');

  // const getFields = () => {
  //   if (hook.localType === 'company') {
  //     return (
  //       <RHFAutocomplete
  //         sx={{ mt: 2 }}
  //         options={
  //           hook.companies?.map((company: any) => ({
  //             value: company._id,
  //             label: company.name,
  //           })) || []
  //         }
  //         name="company"
  //         label="Empresa"
  //       />
  //     );
  //   }

  //   if (hook.localType === 'local') {
  //     return (
  //       <RHFAutocomplete
  //         sx={{ mt: 2 }}
  //         options={
  //           hook.renderedLocOptions.filter(
  //             (op: { value: string; label: string }) => op.value !== 'all'
  //           ) || []
  //         }
  //         name="local"
  //         label="Localização"
  //       />
  //     );
  //   }

  //   return null;
  // };

  const getTextFromHtml = (html: string) => {
    if (!html) return '';
    // Remove tags HTML e entidades
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  function lpEventsForm() {
    return (
      <>
        <RHFSelect
          name="businessUnitId"
          label="Unidade de negócio"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
          disabled
        >
          {hook.businessUnits?.map((option: any, index: number) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <RHFSelect
          name="lpEvent"
          label="Evento LP"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
          disabled
        >
          {hook.lpEventsByBusinessUnit?.map((option: any, index: number) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              Imagem:
            </Typography>
          </Stack>

          <RHFUploadSingleFileFull
            name="image"
            acceptAll={false}
            // @ts-ignore
            accept="image/*"
            onDrop={hook.handleDrop}
            showPreview={false}
          />

          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            * A imagem deve ter no máximo 800x800 pixels e 600kB
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <RHFTextField
            name="name"
            label="Nome"
            inputProps={{
              maxLength: 100,
            }}
            onChange={(e) => {
              const { value } = e.target;
              const formatted = value.charAt(0).toUpperCase() + value.slice(1);
              hook.setValue('name', formatted);
            }}
          />
          <Typography
            variant="caption"
            color={hook.watch('name')?.length > 75 ? 'warning.main' : 'text.secondary'}
            sx={{ textAlign: 'right' }}
          >
            {hook.watch('name')?.length || 0}/100 caracteres
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <RHFTextArea
            name="description"
            label="Descrição"
            inputProps={{
              maxLength: 300,
            }}
          />
          <Typography
            variant="caption"
            color={hook.watch('description')?.length > 250 ? 'warning.main' : 'text.secondary'}
            sx={{ textAlign: 'right' }}
          >
            {hook.watch('description')?.length || 0}/300 caracteres
          </Typography>
        </Stack>

        <RHFSelect
          name="category"
          label="Categoria"
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
        >
          {hook.categories?.map((option: any, index: number) => (
            <MenuItem key={index + 20} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <RHFSelect
          name="theme"
          label="Tema"
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
        >
          {hook.themes?.map((option: any, index: number) => (
            <MenuItem key={index + 20} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <RHFEditor
          name="rules"
          label="Regras"
          onChange={(value) => {
            const textOnly = getTextFromHtml(value);
            if (textOnly.length <= 1000) {
              hook.setValue('rules', value);
            }
          }}
        />
        <Typography
          variant="caption"
          color={
            getTextFromHtml(hook.watch('rules') || '').length > 800
              ? 'warning.main'
              : 'text.secondary'
          }
          sx={{ textAlign: 'right' }}
        >
          {getTextFromHtml(hook.watch('rules') || '').length}/1000 caracteres
        </Typography>

        <Stack>
          <Stack
            direction="row"
            width="100%"
            sx={{
              alignItems: 'center',
            }}
            spacing={4}
          >
            <Controller
              name="startDate"
              control={hook.control}
              render={({ field }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Início"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: '',
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="endDate"
              control={hook.control}
              render={({ field }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Fim"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: '',
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  {...field}
                />
              )}
            />
          </Stack>
          {!hook.isValidEndDate && (
            <Typography
              sx={{
                pl: 1.8,
                pt: 1,
              }}
              variant="caption"
              color="error"
            >
              Data de término deve ser maior ou igual do que a data de início
            </Typography>
          )}
        </Stack>

        {/* <RHFTextField name="location" label="Local" /> */}

        {/* <RHFMultiSelect
          name="highlightCategory"
          options={hook.filteredHighlights}
          label="Selecionar Destaque"
          chip
          checkbox
        /> */}

        <Stack>
          {/* <Typography variant="body1" fontWeight="bold">
            Tipo de Local:
          </Typography>
          <RHFRadioGroupLegacy
            name="localType"
            options={hook.LOCAL_TYPE_OPTIONS}
            getOptionLabel={hook.LOCAL_TYPE_LABEL}
          />
          {getFields()} */}
          <RHFAutocomplete
            options={
              hook.renderedLocOptions.filter(
                (op: { value: string; label: string }) => op.value !== 'all'
              ) || []
            }
            disabled={isLpExhibitor}
            name="local"
            label="Localização"
          />
        </Stack>

        <Stack>
          <Typography variant="body1" fontWeight="bold">
            Tipo de Resgate:
          </Typography>
          <RHFRadioGroupLegacy
            disabled={hook.isEdit}
            name="rewardType"
            options={hook.PRODUCT_TYPE_OPTIONS}
            getOptionLabel={hook.PRODUCT_TYPE_LABEL}
          />
        </Stack>

        {hook.reward === 'code' && (
          <Stack>
            <Typography>Tipo de exibição dos códigos/links:</Typography>
            <RHFRadioGroupProduct
              name="codeShowType"
              options={hook.PRODUCT_CODE_OPTIONS}
              getOptionLabel={hook.PRODUCT_CODE_LABEL}
            />
          </Stack>
        )}

        <Stack spacing={2}>
          {hook.reward === 'simple' ? (
            <RHFTextField type="number" name="totalQuantity" label="Quantidade/Estoque" />
          ) : (
            <Stack spacing={2}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
                spacing={2}
              >
                <Button variant="soft" onClick={() => hook.setOpen(true)}>
                  Cadastrar Códigos/Links
                </Button>
                <Typography variant="overline">
                  {hook?.codesEdited
                    ? `${hook?.codes?.length || 0} Códigos/Links Cadastrados`
                    : `${hook.codes.length * hook.redeemCodeQuantity || 0} Códigos/Links Cadastrados`}
                </Typography>
              </Stack>

              <RHFTextField
                disabled={hook.isEdit}
                name="redeemCodeQuantity"
                label="Quantidade de códigos/links por resgate"
                type="number"
              />
            </Stack>
          )}
          <RHFTextField
            type="number"
            name="expirationDays"
            label="Tempo de retirada após resgate (em dias)"
          />
        </Stack>

        <RHFTextField
          name="userPurchaseLimit"
          label="Quantidade de resgates por usuário"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
        />

        {/* <RHFTextField name="priority" label="Nível de prioridade" type="number" /> */}

        {/* <Stack>
          <Typography variant="body1" fontWeight="bold">
            Status:
          </Typography>
          <RHFRadioGroupLegacy
            name="status"
            options={hook.STATUS_OPTIONS}
            getOptionLabel={hook.STATUS_LABEL}
          />
        </Stack> */}
      </>
    );
  }

  return (
    <>
      {hook?.productDetails?.lpEventsData.editComment &&
        hook?.productDetails?.lpEventsData?.approvalStatus === 'pending' &&
        isCnAdmin && (
          <>
            <Card sx={{ p: 4 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Motivo da Edição</Typography>
                <TextField
                  disabled
                  value={hook?.productDetails?.lpEventsData.editComment}
                  multiline
                  minRows={4}
                  maxRows={6}
                />
              </Stack>
            </Card>
            <Box height={32} />
          </>
        )}
      {hook?.productDetails?.lpEventsData?.declineComment &&
        hook?.productDetails?.lpEventsData?.approvalStatus === 'declined' && (
          <>
            <Card sx={{ p: 4 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Motivo da Recusa</Typography>
                <TextField
                  disabled
                  value={hook?.productDetails?.lpEventsData.declineComment}
                  multiline
                  minRows={4}
                  maxRows={6}
                />
              </Stack>
            </Card>
            <Box height={32} />
          </>
        )}
      <Card>
        <CardContent>
          <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
            <Stack spacing={3}>
              {/* <RHFSelect
              name="exhibitionEnv"
              label="Ambiente de exibição"
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '3px',
                }}
                disabled
                >
                {hook.EXHIBITION_ENV_OPTIONS?.map((option: any, index: number) => (
                  <MenuItem key={index + 20} value={option.value}>
                  {option.label}
                  </MenuItem>
                  ))}
                  </RHFSelect> */}
              {lpEventsForm()}
              {hasRequiredRole && (
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  {hook.isEdit && hook?.approvalStatus === 'approved' && (
                    <LoadingButton
                      onClick={() => hook.handleActivateAndInactivate()}
                      variant="contained"
                      disabled={disableUpdate}
                    >
                      {hook?.productStatus === 'active' ? 'Inativar Produto' : 'Ativar Produto'}
                    </LoadingButton>
                  )}
                  <LoadingButton
                    {...(!hook.isEdit
                      ? { type: 'submit' }
                      : { onClick: () => hook.setOpenComment(true) })}
                    variant="contained"
                    loading={hook.isSubmitting}
                    disabled={disableUpdate}
                  >
                    {hook.title}
                  </LoadingButton>
                </Stack>
              )}
              {isCnAdmin && hook.isEdit && (
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    disabled={hook?.approvalStatus === 'approved'}
                    onClick={() => hook.handleApprove()}
                    variant="contained"
                  >
                    Aprovar Produto
                  </LoadingButton>
                  <LoadingButton
                    disabled={hook?.approvalStatus === 'declined'}
                    onClick={() => hook.handleRefuse()}
                    variant="contained"
                  >
                    Recusar Produto
                  </LoadingButton>
                </Stack>
              )}
            </Stack>
            <CommentDialog hook={hook} />
          </FormProvider>
          <RefuseDialog hook={hook} />
          <ApproveDialog hook={hook} />
        </CardContent>
      </Card>
    </>
  );
}
